/**
 * WhisperPanel — parent's "悄悄话" composer.
 *
 * Writes a one-line message that the AI pet will deliver verbatim the next
 * time it speaks proactively (cron sweep, every 3h). The kid never learns
 * that the parent injected it — the pet just says it.
 *
 * Auto-refreshes the history list after create/cancel so the parent sees
 * delivery state ("已传达 / 等待传达 / 已取消").
 */
import { useEffect, useState } from 'react';
import { api, ApiCallError } from '../api/client';

const MAX_LEN = 120;

type Whisper = {
  id: number;
  text: string;
  created_at: number;
  delivered_at: number | null;
  expires_at: number;
  cancelled_at: number | null;
};

function whisperStatus(w: Whisper, now: number): { label: string; tone: 'pending' | 'delivered' | 'expired' | 'cancelled' } {
  if (w.cancelled_at) return { label: '已取消', tone: 'cancelled' };
  if (w.delivered_at) return { label: '已传达', tone: 'delivered' };
  if (w.expires_at < now) return { label: '已过期', tone: 'expired' };
  return { label: '等待传达', tone: 'pending' };
}

function relTime(ts: number, now: number) {
  const d = now - ts;
  if (d < 60_000) return '刚刚';
  if (d < 3600_000) return `${Math.floor(d / 60_000)} 分钟前`;
  if (d < 86400_000) return `${Math.floor(d / 3600_000)} 小时前`;
  return `${Math.floor(d / 86400_000)} 天前`;
}

export default function WhisperPanel({ childId, childName }: { childId: number; childName: string }) {
  const [text, setText] = useState('');
  const [whispers, setWhispers] = useState<Whisper[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const now = Date.now();

  async function refresh() {
    try {
      const r = await api.listWhispers(childId);
      setWhispers(r.whispers);
    } catch (e) {
      setErr(e instanceof ApiCallError ? e.message : '加载失败');
    }
  }

  useEffect(() => { void refresh(); }, [childId]);

  async function submit() {
    const trimmed = text.trim();
    if (!trimmed) return;
    setBusy(true); setErr(null);
    try {
      await api.createWhisper(childId, trimmed);
      setText('');
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiCallError ? e.message : '保存失败');
    } finally { setBusy(false); }
  }

  async function cancel(id: number) {
    setBusy(true); setErr(null);
    try {
      await api.cancelWhisper(id);
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiCallError ? e.message : '取消失败');
    } finally { setBusy(false); }
  }

  const remaining = MAX_LEN - text.length;
  const pendingCount = whispers.filter((w) => !w.delivered_at && !w.cancelled_at && w.expires_at > now).length;

  return (
    <div className="card">
      <div style={{ fontWeight: 700, marginBottom: 6 }}>💌 悄悄话</div>
      <div className="muted" style={{ fontSize: 13, marginBottom: 12, lineHeight: 1.6 }}>
        写一句话，<b>立即</b>通过宠物的嘴巴传给 {childName} —— iPad 锁屏会弹出消息提醒，
        {childName} 看到的就像宠物自己说的，不会发现是你写的。
      </div>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, MAX_LEN))}
        rows={2}
        placeholder="例：今天我看到你认真做家务，特别为你骄傲！"
        disabled={busy}
        style={{
          width: '100%', padding: 10, borderRadius: 10,
          border: '1px solid var(--border)', fontSize: 14, resize: 'vertical',
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
        <span className="muted" style={{ fontSize: 12 }}>{remaining} 字</span>
        <button className="btn btn-primary" onClick={() => void submit()} disabled={busy || !text.trim()}>
          {busy ? '发送中…' : '✉️ 立即送达'}
        </button>
      </div>
      {err && <div className="error-box" style={{ marginTop: 8 }}>{err}</div>}

      {whispers.length > 0 && (
        <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
          <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
            历史记录 · 等待中 {pendingCount}
          </div>
          {whispers.slice(0, 8).map((w) => {
            const status = whisperStatus(w, now);
            return (
              <div
                key={w.id}
                style={{
                  padding: '8px 0', borderBottom: '1px solid var(--border)',
                  display: 'flex', gap: 10, alignItems: 'flex-start',
                }}
              >
                <div style={{ flex: 1, fontSize: 14, lineHeight: 1.5 }}>
                  <div>{w.text}</div>
                  <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                    <span
                      className="tag"
                      style={{
                        marginRight: 6,
                        background:
                          status.tone === 'delivered' ? '#dcfce7'
                          : status.tone === 'pending' ? '#fef3c7'
                          : status.tone === 'cancelled' ? '#e5e7eb'
                          : '#fee2e2',
                        color:
                          status.tone === 'delivered' ? '#166534'
                          : status.tone === 'pending' ? '#92400e'
                          : status.tone === 'cancelled' ? '#374151'
                          : '#b91c1c',
                      }}
                    >
                      {status.label}
                    </span>
                    {relTime(w.created_at, now)}
                  </div>
                </div>
                {status.tone === 'pending' && (
                  <button
                    className="btn btn-secondary"
                    style={{ fontSize: 12, padding: '4px 10px' }}
                    onClick={() => void cancel(w.id)}
                    disabled={busy}
                  >
                    取消
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
